import { faBars, faCircleExclamation } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert } from "@mui/material";
import React from "react";
import {
  Button,
  Col,
  Form,
  ListGroup,
  Modal,
  Row,
  Stack,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { CustomFlashpack, FlashpackItem, Lesson } from "../../common/api";
import { BackNavRow, PageContainer } from "../../common/CommonStyles";
import { ApiContext } from "../../common/context/ApiContext";
import { useLoadingIndicator } from "../../common/context/LoadingIndicatorContext";
import {
  getFlashpackAddItemTitle,
  isMathFlashpackType,
} from "../../common/utils";
import { FlashpackBuilder } from "./FlashpackBuilder";
import { MathFlashpackBuilder } from "./MathFlashpackBuilder";
import {
  AnalyticsContext,
  FlashpackSettingsDisableRandomizationTapEvent,
  FlashpackSettingsEditWordsButtonTapEvent,
  FlashpackSettingsEnableRandomizationTapEvent,
  ViewFlashpackSettingsEvent,
} from "../../common/context/AnalyticsContext";

const enum Mode {
  SETTINGS = 0,
  EDITING = 1,
}

export const FlashpackSettings = ({
  customFlashpackId,
  lesson,
  onCanceled,
  onChanged,
}: {
  customFlashpackId: number;
  lesson: Lesson;
  onCanceled: () => void;
  onChanged: () => void;
}) => {
  const { t } = useTranslation();
  const { startLoading, stopLoading } = useLoadingIndicator(true);
  const { getCustomFlashpack, modifyCustomFlashpack } =
    React.useContext(ApiContext);
  const [flashpack, setFlashpack] = React.useState<CustomFlashpack>();
  const [mode, setMode] = React.useState(Mode.SETTINGS);
  const [canSave, setCanSave] = React.useState(false);
  const [tempRandomizeCardOrder, setTempRandomizeCardOrder] =
    React.useState(false);
  const [tempTitle, setTempTitle] = React.useState(lesson.title);
  const [tempDeckItems, setTempDeckItems] = React.useState<FlashpackItem[]>([]);
  const [isDeckModified, setIsDeckModified] = React.useState(false);
  const [invalidTempDeckErrorMessage, setInvalidTempDeckErrorMessage] =
    React.useState<string | null>(null);
  const [flashpackItemsTitle, setFlashpackItemsTitle] = React.useState("");
  const [isUnsavedModalOpen, setIsUnsavedModalOpen] = React.useState(false);
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);
  const { logEvent } = React.useContext(AnalyticsContext);

  React.useEffect(() => {
    logEvent(new ViewFlashpackSettingsEvent(customFlashpackId));
  }, [logEvent, customFlashpackId]);

  const doSave = React.useCallback(() => {
    startLoading();
    modifyCustomFlashpack(customFlashpackId, {
      title: tempTitle,
      randomize_card_order: tempRandomizeCardOrder,
      items: tempDeckItems.map((i) => i.item),
    }).then(() => {
      stopLoading();
      onChanged();
    });
  }, [
    customFlashpackId,
    modifyCustomFlashpack,
    startLoading,
    stopLoading,
    tempDeckItems,
    tempRandomizeCardOrder,
    tempTitle,
    onChanged,
  ]);

  // Recalculate whether the deck is "dirty" every time it changes
  React.useEffect(() => {
    if (flashpack !== undefined) {
      setIsDeckModified(
        flashpack.content.items.length !== tempDeckItems.length ||
          !flashpack.content.items.every(
            (item, index) => item.item === tempDeckItems[index].item
          )
      );
    }
  }, [flashpack, tempDeckItems, setIsDeckModified]);

  // Recalculate whether the flashpack is dirty every time one of its modifiable props changes
  React.useEffect(() => {
    setCanSave(
      flashpack !== undefined &&
        invalidTempDeckErrorMessage === null &&
        tempTitle.length > 0 &&
        (lesson.title !== tempTitle ||
          flashpack.randomize_card_order !== tempRandomizeCardOrder ||
          isDeckModified)
    );
  }, [
    setCanSave,
    lesson,
    flashpack,
    tempRandomizeCardOrder,
    tempTitle,
    isDeckModified,
    invalidTempDeckErrorMessage,
  ]);

  // Load the custom flashpack on the first render of the component
  React.useEffect(() => {
    startLoading();
    getCustomFlashpack(customFlashpackId).then((flashpack) => {
      setFlashpack(flashpack);
      setTempRandomizeCardOrder(flashpack.randomize_card_order);
      setTempDeckItems([...flashpack.content.items]);
      const title = getFlashpackAddItemTitle(t, flashpack.flashpack_type);
      setFlashpackItemsTitle(title);
      stopLoading();
    });
  }, [
    t,
    startLoading,
    getCustomFlashpack,
    customFlashpackId,
    setFlashpack,
    setTempDeckItems,
    stopLoading,
  ]);

  if (!flashpack) {
    return <></>;
  }

  return mode === Mode.SETTINGS ? (
    <>
      {invalidTempDeckErrorMessage !== null && (
        <Alert
          icon={
            <FontAwesomeIcon icon={faCircleExclamation} fontSize="inherit" />
          }
          severity="error"
          style={{ marginBottom: "15px" }}
        >
          {invalidTempDeckErrorMessage}
        </Alert>
      )}
      <PageContainer style={{ maxWidth: "800px" }}>
        <div className="my-2">
          <BackNavRow
            label={t("lesson_nav_back")}
            callback={() => {
              if (canSave) {
                setIsUnsavedModalOpen(true);
              } else {
                onCanceled();
              }
            }}
            rightChildren={
              canSave ? (
                <Link
                  to=""
                  onClick={(event) => {
                    doSave();
                    event.preventDefault();
                  }}
                  style={{ textDecoration: "none" }}
                >
                  {t("generic_save")}
                </Link>
              ) : (
                <div>{t("generic_save")}</div>
              )
            }
          >
            <h2>{t("flashpacks_edit_settings_title")}</h2>
          </BackNavRow>
        </div>
        <Stack className="mb-3" direction="horizontal" gap={3}>
          <div>{t("flashpacks_edit_settings_randomize")}</div>
          <Form.Check
            checked={tempRandomizeCardOrder}
            onChange={(e) => {
              logEvent(
                e.target.checked
                  ? new FlashpackSettingsEnableRandomizationTapEvent()
                  : new FlashpackSettingsDisableRandomizationTapEvent()
              );
              setTempRandomizeCardOrder(e.target.checked);
            }}
          />
        </Stack>
        <Stack className="mb-4" direction="horizontal" gap={3}>
          <div>{t("flashpacks_edit_settings_name")}</div>
          <Form.Control
            style={{ width: "300px" }}
            placeholder="Name"
            value={tempTitle}
            onChange={(e) => setTempTitle(e.target.value)}
          />
        </Stack>
        <hr />
        <Row>
          <Col>
            <h4>{t("flashpacks_edit_settings_cards_title")}:</h4>
          </Col>
        </Row>
        <Row>
          <Col xs={6} sm={4}>
            <ListGroup>
              {tempDeckItems.map((item: FlashpackItem, index) => (
                <ListGroup.Item
                  key={item.item}
                  draggable={!tempRandomizeCardOrder}
                  onDragStart={
                    !tempRandomizeCardOrder
                      ? () => setDraggedIndex(index)
                      : undefined
                  }
                  onDragOver={
                    !tempRandomizeCardOrder
                      ? (e) => {
                          e.preventDefault();
                          if (draggedIndex !== null && draggedIndex !== index) {
                            const updated = [...tempDeckItems];
                            const [moved] = updated.splice(draggedIndex, 1);
                            updated.splice(index, 0, moved);
                            setTempDeckItems(updated);
                            setDraggedIndex(index);
                          }
                        }
                      : undefined
                  }
                  onDragEnd={
                    !tempRandomizeCardOrder
                      ? () => setDraggedIndex(null)
                      : undefined
                  }
                  style={
                    !tempRandomizeCardOrder
                      ? {
                          cursor: "grab",
                          opacity: draggedIndex === index ? 0.5 : 1,
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                        }
                      : undefined
                  }
                >
                  {!tempRandomizeCardOrder && (
                    <FontAwesomeIcon
                      icon={faBars}
                      style={{ color: "#aaa", flexShrink: 0 }}
                    />
                  )}
                  {item.item}
                </ListGroup.Item>
              ))}
            </ListGroup>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Button
              onClick={() => {
                logEvent(
                  new FlashpackSettingsEditWordsButtonTapEvent(
                    flashpack.flashpack_type,
                    flashpack.id
                  )
                );
                setMode(Mode.EDITING);
              }}
            >
              {t("flashpacks_edit_settings_edit")}
            </Button>
          </Col>
        </Row>
      </PageContainer>

      {/* Modal: confirm unsaved changes upon exit */}
      <Modal
        show={isUnsavedModalOpen}
        onHide={() => setIsUnsavedModalOpen(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("flashpacks_edit_settings_unsaved_changes_title")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Stack direction="horizontal" gap={3}>
            <Button
              className="ms-auto"
              variant="primary"
              onClick={() => {
                setIsUnsavedModalOpen(false);
                doSave();
              }}
            >
              {t("flashpacks_edit_settings_do_save")}
            </Button>
            <Button
              className="me-auto"
              variant="secondary"
              onClick={() => {
                setIsUnsavedModalOpen(false);
                onCanceled();
              }}
            >
              {t("flashpacks_edit_settings_do_discard")}
            </Button>
          </Stack>
        </Modal.Body>
      </Modal>
    </>
  ) : (
    <PageContainer>
      <BackNavRow
        label={t("generic_nav_back")}
        callback={() => setMode(Mode.SETTINGS)}
      >
        <h2>{t("flashpacks_add_items", { items: flashpackItemsTitle })}</h2>
      </BackNavRow>
      {isMathFlashpackType(flashpack.flashpack_type) ? (
        <MathFlashpackBuilder
          flashpackType={flashpack.flashpack_type}
          deckItems={tempDeckItems}
          setDeckItems={setTempDeckItems}
          setInvalidDeckErrorMessage={setInvalidTempDeckErrorMessage}
        />
      ) : (
        <FlashpackBuilder
          flashpackType={flashpack.flashpack_type}
          deckItems={tempDeckItems}
          setDeckItems={setTempDeckItems}
          setInvalidDeckErrorMessage={setInvalidTempDeckErrorMessage}
        />
      )}
    </PageContainer>
  );
};
